import { Button, Flex } from "@capioweb/ui-components"
import React, { useState } from "react"
import { StringProperty } from "../../@types/EpiFoundationSpa/Property"
import { LocalesContext } from "../../context"
import { BlockCommonLayout } from "../../utils/layout"
import {
	MyPagesPersonalizedArticleBlock,
	MyPagesPersonalizedArticleProps,
} from "./MyPagesPersonalizedArticleBlock"
import { ArticlesFilter } from "./ArticlesFilter"
import { filterArticles } from "./utils/filterArticles"
import { resolveFilterItems } from "./utils/resolveFilterItems"
import { PropertyCategory } from "../../@types/CapioWeb/core"

interface MyPagesPersonalizedArticlesBlockProps {
	heading: StringProperty
	articles: MyPagesPersonalizedArticleProps[]
	categories: PropertyCategory
	recommendedArticles?: {
		description: string
		items: string[] // List of article guids
	}
	savedArticles?: string[] // List of article guids
	modelType: string
}

const MyPagesPersonalizedArticlesBlock: React.FC<MyPagesPersonalizedArticlesBlockProps> =
	({
		articles,
		categories,
		recommendedArticles,
		savedArticles,
		modelType,
		...rest
	}) => {
		const { translate, translationKeys } = React.useContext(LocalesContext)

		const isSavedArticlesBlock = modelType === "MyPagesSavedArticlesBlockModel"

		const numberOfInitialArticles = 4

		// Filter states
		const filterAllOption = translate(translationKeys.all) && "Alla"
		const filterRecommendedOption =
			recommendedArticles?.description ?? "Rekommenderat"
		const [selectedFilterItem, setSelectedFilterItem] =
			useState(filterAllOption)
		const [showAll, setShowAll] = useState(false)
		const [filteredArticles, setFilteredArticles] = React.useState(articles)

		const onFilterItemClicked = (filterItem: string) => {
			setSelectedFilterItem(filterItem)
			if (filterItem === filterAllOption) {
				setFilteredArticles(articles)
			} else if (filterItem === filterRecommendedOption) {
				setFilteredArticles(
					articles.filter((article) =>
						recommendedArticles.items.includes(article.contentLink.guidValue)
					)
				)
			} else {
				setFilteredArticles(filterArticles(articles, filterItem))
			}
		}

		const onShowMoreButtonClicked = () => {
			setShowAll(true)
		}

		function getFilteredArticles() {
			if (showAll) {
				return filteredArticles
			} else {
				return filteredArticles.slice(0, numberOfInitialArticles)
			}
		}

		return (
			<BlockCommonLayout {...rest}>
				<Flex w={1} flexDirection={"column"}>
					{!isSavedArticlesBlock && (
						<ArticlesFilter
							onFilterItemClicked={onFilterItemClicked}
							filterItems={[
								filterAllOption,
								...resolveFilterItems(categories),
								filterRecommendedOption,
							]}
							selectedItem={selectedFilterItem}
						/>
					)}
					<Flex
						w={1}
						flexWrap="wrap"
						justifyContent="space-between"
						alignContent="space-between"
						columnGap={{ m: 3 }}
						rowGap={{ _: 2, m: 3 }}
					>
						{filteredArticles.length > 0 &&
							getFilteredArticles().map((article, i) => {
								return (
									<Flex
										key={i}
										maxw={{ l: "calc(100% / 3 * 2 - 12px)" }}
										flexGrow={1}
										flexShrink={1}
										flexBasis={{
											_: "100%",
											m: i === 0 ? "calc(100%)" : "calc(50% - 24px)",
											l:
												i === 0
													? "calc(100% / 3 * 2 - 12px)"
													: "calc(100% / 3 - 24px)",
										}}
									>
										<MyPagesPersonalizedArticleBlock
											{...article}
											isSavedArticle={savedArticles?.includes(
												article.contentLink.guidValue
											)}
										/>
									</Flex>
								)
							})}
					</Flex>
					{filteredArticles.length > numberOfInitialArticles && (
						<Flex w={1} justifyContent="center">
							<Button
								display={
									filteredArticles.length < numberOfInitialArticles || showAll
										? "none"
										: "inline-flex"
								}
								variant="ghost"
								textColor="navy"
								icon="plus"
								onClick={onShowMoreButtonClicked}
								mt={{ _: 2, l: 5 }}
							>
								{translate(translationKeys.showMore) &&
									translate(translationKeys.showMore)}
							</Button>
						</Flex>
					)}
				</Flex>
			</BlockCommonLayout>
		)
	}

export { MyPagesPersonalizedArticlesBlock }
